import React, { Component, ErrorInfo, ReactNode, useState } from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, Home, AlertOctagon, Send, Bug, Loader2, Code, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import parkRowLogo from '@/assets/park-row-logo.png';
import { enrichStackTrace, createErrorReport, type StackFrame } from '@/lib/stacktrace';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  autoReloadCountdown: number | null;
  isReporting: boolean;
  reportSubmitted: boolean;
  parsedStack: {
    frames: StackFrame[];
    appFrames: StackFrame[];
    vendorFrames: StackFrame[];
    summary: string;
  } | null;
}

// Dev mode test component to trigger errors
const ErrorTriggerButton = () => {
  const [shouldError, setShouldError] = useState(false);
  
  if (shouldError) {
    throw new Error('🧪 Test Error: This is a manually triggered error to test the Error Boundary UI.');
  }
  
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }
  
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => setShouldError(true)}
      className="fixed bottom-4 right-4 z-50 gap-2 border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
    >
      <Bug className="w-4 h-4" />
      Test Error Boundary
    </Button>
  );
};

export { ErrorTriggerButton };

export class ErrorBoundary extends Component<Props, State> {
  private countdownInterval: NodeJS.Timeout | null = null;

  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    autoReloadCountdown: null,
    isReporting: false,
    reportSubmitted: false,
    parsedStack: null,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    // Parse stack trace immediately
    const enriched = error.stack ? enrichStackTrace(error.stack) : null;
    return { 
      hasError: true, 
      error, 
      errorInfo: null,
      parsedStack: enriched ? {
        frames: enriched.frames,
        appFrames: enriched.appFrames,
        vendorFrames: enriched.vendorFrames,
        summary: enriched.summary,
      } : null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('🚨 ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);
    this.setState({ errorInfo });
    
    // Show toast notification
    toast.error('Something went wrong. Our team has been notified.');
    
    // Auto-submit error report to database
    this.submitErrorReport(error, errorInfo);
  }

  componentWillUnmount() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  private submitErrorReport = async (error: Error, errorInfo: ErrorInfo | null) => {
    const report = createErrorReport(error, errorInfo?.componentStack);
    
    try {
      const { error: submitError } = await supabase.functions.invoke('report-error', {
        body: {
          error_message: report.message,
          error_stack: report.stack,
          stack_frames: report.stackFrames,
          stack_summary: report.summary,
          component_stack: report.componentStack,
          url: report.url,
          user_agent: report.userAgent,
          timestamp: report.timestamp,
        }
      });

      if (submitError) {
        console.error('Failed to submit error report:', submitError);
      } else {
        console.log('✅ Error report submitted automatically');
      }
    } catch (err) {
      console.error('Error submitting report:', err);
    }
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private startAutoReload = () => {
    this.setState({ autoReloadCountdown: 10 });
    this.countdownInterval = setInterval(() => {
      this.setState((prev) => {
        if (prev.autoReloadCountdown === 1) {
          if (this.countdownInterval) clearInterval(this.countdownInterval);
          window.location.reload();
          return { ...prev };
        }
        return { ...prev, autoReloadCountdown: (prev.autoReloadCountdown || 10) - 1 };
      });
    }, 1000);
  };

  private cancelAutoReload = () => {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    this.setState({ autoReloadCountdown: null });
  };

  private handleReportError = async () => {
    const { error, errorInfo } = this.state;
    
    this.setState({ isReporting: true });
    
    const report = error ? createErrorReport(error, errorInfo?.componentStack) : null;
    const errorDetails = {
      error_message: report?.message || 'Unknown error',
      error_stack: report?.stack,
      stack_frames: report?.stackFrames || [],
      stack_summary: report?.summary || 'Unknown location',
      component_stack: report?.componentStack,
      url: report?.url || window.location.href,
      user_agent: report?.userAgent || navigator.userAgent,
      timestamp: report?.timestamp || new Date().toISOString(),
    };
    
    // Log to console
    console.log('📋 Error Report:', JSON.stringify(errorDetails, null, 2));
    
    try {
      const { error: submitError } = await supabase.functions.invoke('report-error', {
        body: errorDetails
      });

      if (submitError) {
        toast.error('Failed to submit report. Please try again.');
        console.error('Submit error:', submitError);
      } else {
        toast.success('Error report submitted. Thank you for helping us improve!');
        this.setState({ reportSubmitted: true });
      }
    } catch (err) {
      toast.error('Failed to submit report.');
      console.error('Error:', err);
    } finally {
      this.setState({ isReporting: false });
    }
  };

  public render() {
    if (this.state.hasError) {
      const { autoReloadCountdown, isReporting, reportSubmitted } = this.state;

      return (
        <div className="min-h-screen bg-gradient-to-b from-[#0a0a0a] via-[#1a1a1a] to-[#0a0a0a] flex items-center justify-center p-4">
          <div className="max-w-lg w-full text-center space-y-8">
            {/* Logo */}
            <div className="flex justify-center">
              <img 
                src={parkRowLogo} 
                alt="Park Row" 
                className="h-16 w-auto opacity-90"
              />
            </div>

            {/* Error Icon with glow */}
            <div className="relative mx-auto w-24 h-24">
              <div className="absolute inset-0 bg-red-500/20 rounded-full blur-xl animate-pulse" />
              <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-red-900/50 to-red-950/50 border border-red-500/30 flex items-center justify-center">
                <AlertOctagon className="w-12 h-12 text-red-400" />
              </div>
            </div>

            {/* Error Message */}
            <div className="space-y-3">
              <h1 className="text-3xl font-bold text-white tracking-tight">
                Something went wrong
              </h1>
              <p className="text-lg text-neutral-400 font-medium">
                We're fixing it faster than a Texas two-step.
              </p>
              <p className="text-sm text-neutral-500">
                Don't worry — your data is safe. Try reloading or head back home.
              </p>
            </div>

            {/* Auto-reload countdown */}
            {autoReloadCountdown !== null && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                <p className="text-amber-400 text-sm">
                  Auto-reloading in <span className="font-bold">{autoReloadCountdown}</span> seconds...
                  <button 
                    onClick={this.cancelAutoReload}
                    className="ml-2 underline hover:text-amber-300 transition-colors"
                  >
                    Cancel
                  </button>
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                onClick={this.handleReload} 
                className="gap-2 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white border-0 shadow-lg shadow-amber-900/20"
                size="lg"
              >
                <RefreshCw className="w-4 h-4" />
                Reload Page
              </Button>
              <Button 
                variant="outline" 
                onClick={this.handleGoHome} 
                className="gap-2 border-neutral-700 text-neutral-300 hover:bg-neutral-800 hover:text-white"
                size="lg"
              >
                <Home className="w-4 h-4" />
                Go Home
              </Button>
            </div>

            {/* Auto-reload trigger */}
            {autoReloadCountdown === null && (
              <button
                onClick={this.startAutoReload}
                className="text-sm text-neutral-500 hover:text-neutral-400 transition-colors underline"
              >
                Enable auto-reload (10s)
              </button>
            )}

            {/* Report Button */}
            <div className="pt-4 border-t border-neutral-800">
              <Button
                variant="ghost"
                size="sm"
                onClick={this.handleReportError}
                disabled={isReporting || reportSubmitted}
                className="text-neutral-500 hover:text-neutral-300 gap-2"
              >
                {isReporting ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Submitting...
                  </>
                ) : reportSubmitted ? (
                  <>
                    <Send className="w-3 h-3" />
                    Report submitted
                  </>
                ) : (
                  <>
                    <Send className="w-3 h-3" />
                    Report this issue
                  </>
                )}
              </Button>
            </div>

            {/* Technical Details (dev only) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="text-left space-y-3">
                {/* Error Summary */}
                {this.state.parsedStack && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-amber-400 text-sm font-medium">
                      <Code className="w-4 h-4" />
                      {this.state.parsedStack.summary}
                    </div>
                  </div>
                )}
                
                {/* Parsed Stack Frames */}
                <details className="bg-neutral-900/50 border border-neutral-800 rounded-lg p-4 text-sm">
                  <summary className="cursor-pointer text-neutral-400 font-medium hover:text-neutral-300 flex items-center gap-2">
                    <ChevronRight className="w-4 h-4 details-open:rotate-90 transition-transform" />
                    App Stack Trace ({this.state.parsedStack?.appFrames.length || 0} frames)
                  </summary>
                  <div className="mt-3 space-y-1">
                    {this.state.parsedStack?.appFrames.map((frame, i) => (
                      <div key={i} className="text-xs font-mono p-2 bg-neutral-950/50 rounded border border-neutral-800">
                        <span className="text-blue-400">{frame.functionName || '<anonymous>'}</span>
                        <span className="text-neutral-500"> in </span>
                        <span className="text-green-400">{frame.fileName?.split('/').pop()}</span>
                        <span className="text-neutral-500">:</span>
                        <span className="text-amber-400">{frame.lineNumber}</span>
                        <span className="text-neutral-500">:</span>
                        <span className="text-amber-400">{frame.columnNumber}</span>
                      </div>
                    ))}
                    {(!this.state.parsedStack?.appFrames.length) && (
                      <div className="text-neutral-500 text-xs">No app frames found</div>
                    )}
                  </div>
                </details>

                {/* Vendor Frames (collapsed by default) */}
                {this.state.parsedStack?.vendorFrames.length ? (
                  <details className="bg-neutral-900/50 border border-neutral-800 rounded-lg p-4 text-sm">
                    <summary className="cursor-pointer text-neutral-500 font-medium hover:text-neutral-400 flex items-center gap-2">
                      <ChevronRight className="w-4 h-4 details-open:rotate-90 transition-transform" />
                      Vendor Frames ({this.state.parsedStack.vendorFrames.length})
                    </summary>
                    <pre className="mt-3 text-xs text-neutral-500 overflow-auto max-h-32 font-mono">
                      {this.state.parsedStack.vendorFrames.map(f => f.source).join('\n')}
                    </pre>
                  </details>
                ) : null}

                {/* Raw Error */}
                <details className="bg-neutral-900/50 border border-neutral-800 rounded-lg p-4 text-sm">
                  <summary className="cursor-pointer text-neutral-500 font-medium hover:text-neutral-400 flex items-center gap-2">
                    <ChevronRight className="w-4 h-4 details-open:rotate-90 transition-transform" />
                    Raw Error Details
                  </summary>
                  <pre className="mt-3 text-xs text-red-400 overflow-auto max-h-40 font-mono">
                    {this.state.error.message}
                    {this.state.error.stack && `\n\n${this.state.error.stack}`}
                  </pre>
                </details>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
